import { Text, VSpacer } from '@/components/DesignSystem';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { QueryKeys } from '@/constants/QueryKeys';
import { getProgramPeriodLabel } from '@/pages/RetailerOverview/helpers';
import { ReportApi } from '@/utilities/api/ReportApi';
import { RewardsProgramApi } from '@/utilities/api/RewardsProgramApi';
import { ReportEndpoint } from '@api/endpoints';
import { ApiReport } from '@api/interfaces';
import Download from '@mui/icons-material/Download';
import {
  CircularProgress,
  IconButton,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { ReportStatus, ReportType } from '@shared/enums';
import { formatDateTime, formatFileSize, formatMediumDate } from '@shared/utilities/FormatUtils';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

const PAGE_LIMIT = 20;
const POLL_INTERVAL_MS = 5000;

const NonFilterParams = ['language'];
const ProgramYearParams = ['programId', 'rewardsProgramId'];

const isDateRange = (value: unknown): value is ReportEndpoint.DateRange => {
  const range = value as Partial<ReportEndpoint.DateRange> | null;
  return typeof range === 'object'
    && range !== null
    && typeof range.start === 'string'
    && typeof range.end === 'string';
};

export const ReportHistory = ({ retailerId }: { retailerId?: string }) => {
  const { i18n, t } = useTranslation();
  const [page, setPage] = useState(0);
  const [downloadFailed, setDownloadFailed] = useState(false);

  const { data: programs } = useQuery(
    [QueryKeys.LIST_REWARDS_PROGRAMS, retailerId],
    () => RewardsProgramApi.list({ retailerId: retailerId as string }),
    { enabled: !!retailerId },
  );

  const { data } = useQuery(
    [QueryKeys.LIST_REPORTS, retailerId, page],
    () => ReportApi.list(retailerId as string, { limit: PAGE_LIMIT, page }),
    {
      enabled: !!retailerId,
      keepPreviousData: true,
      refetchInterval: (result) => (
        result?.data.some((report) => (
          report.status === ReportStatus.Pending || report.status === ReportStatus.Processing
        )) ? POLL_INTERVAL_MS : false
      ),
    },
  );

  const reportTypeName = (type: ReportType) => ({
    [ReportType.Billing]: t($ => $.reports.reportType.billing.name),
    [ReportType.CustomerOverview]: t($ => $.reports.reportType.customerOverview.name),
    [ReportType.CustomerPoints]: t($ => $.reports.reportType.customerPoints.name),
    [ReportType.TierProgress]: t($ => $.reports.reportType.tierProgress.name),
    [ReportType.TransactionPointsHistory]: (
      t($ => $.reports.reportType.transactionPointsHistory.name)
    ),
  })[type];

  const statusLabel = (status: ReportStatus) => ({
    [ReportStatus.Completed]: t($ => $.reports.history.statusCompleted),
    [ReportStatus.Failed]: t($ => $.reports.history.statusFailed),
    [ReportStatus.Pending]: t($ => $.reports.history.statusQueued),
    [ReportStatus.Processing]: t($ => $.reports.history.statusGenerating),
  })[status];

  const describeFilterValue = (value: unknown) => {
    if (Array.isArray(value)) {
      return `${value.length}`;
    }
    if (isDateRange(value)) {
      return `${formatMediumDate(value.start, i18n)} - ${formatMediumDate(value.end, i18n)}`;
    }
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const describeProgramYear = (programId: unknown) => {
    const program = programs?.find(({ id }) => id === programId);
    return program ? getProgramPeriodLabel(program) : String(programId);
  };

  const describeFilters = (params: ApiReport['params']) => {
    const applied = Object.entries(params ?? {}).filter(([key, value]) => (
      !NonFilterParams.includes(key)
      && value !== null
      && value !== undefined
      && !(Array.isArray(value) && value.length === 0)
    ));
    const programYear = applied.find(([key]) => ProgramYearParams.includes(key));
    const otherFilters = applied.filter(([key]) => !ProgramYearParams.includes(key));
    const descriptions = [
      ...(programYear
        ? [`${t($ => $.reports.history.programYear)}: ${describeProgramYear(programYear[1])}`]
        : []),
      ...otherFilters.map(([key, value]) => `${key}: ${describeFilterValue(value)}`),
    ];
    if (!descriptions.length) {
      return t($ => $.reports.history.noFilters);
    }
    return descriptions.join(', ');
  };

  const download = async (reportId: string) => {
    try {
      const { url } = await ReportApi.getDownloadUrl(retailerId as string, reportId);
      window.location.assign(url);
    } catch {
      setDownloadFailed(true);
    }
  };

  const errorDialog = downloadFailed && (
    <ConfirmDialog
      cancelText={t($ => $.common.actions.close)}
      onCancel={() => setDownloadFailed(false)}
      open
      testID="report-download-failed-dialog"
      title={t($ => $.reports.history.downloadFailedTitle)}
    >
      {t($ => $.reports.history.downloadFailed)}
    </ConfirmDialog>
  );

  const downloadCell = (report: ApiReport) => {
    if (report.status === ReportStatus.Pending || report.status === ReportStatus.Processing) {
      return <CircularProgress size={20} />;
    }
    if (report.status === ReportStatus.Failed) {
      return null;
    }
    return (
      <IconButton
        aria-label={t($ => $.reports.generation.downloadButton)}
        data-testid={`download-report-${report.id}`}
        onClick={() => download(report.id)}
      >
        <Download />
      </IconButton>
    );
  };

  const reports = data?.data ?? [];

  if (!retailerId) {
    return (
      <>
        <VSpacer size="5" />
        <Text category="body-medium">{t($ => $.reports.history.selectRetailer)}</Text>
      </>
    );
  }

  if (!data) {
    return (
      <Stack alignItems="center" pt="24px">
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (!reports.length) {
    return (
      <>
        <VSpacer size="5" />
        <Text category="body-medium">{t($ => $.reports.history.noReports)}</Text>
        {errorDialog}
      </>
    );
  }

  return (
    <>
      <Table size="small" stickyHeader>
        <TableHead
          sx={{
            '& .MuiTableCell-root.MuiTableCell-head': {
              backgroundColor: theme => theme.palette.background.paper,
              borderBottom: theme => `1px solid ${theme.palette.outline}`,
            },
          }}
        >
          <TableRow sx={{ height: '56px' }}>
            <TableCell width={220}>
              <Text category="label-large">{t($ => $.reports.reportName)}</Text>
            </TableCell>
            <TableCell>
              <Text category="label-large">{t($ => $.reports.history.filters)}</Text>
            </TableCell>
            <TableCell width={180}>
              <Text category="label-large">{t($ => $.reports.history.generatedOn)}</Text>
            </TableCell>
            <TableCell width={180}>
              <Text category="label-large">{t($ => $.reports.history.requestedBy)}</Text>
            </TableCell>
            <TableCell width={120}>
              <Text category="label-large">{t($ => $.reports.history.status)}</Text>
            </TableCell>
            <TableCell width={100}>
              <Text category="label-large">{t($ => $.reports.history.size)}</Text>
            </TableCell>
            <TableCell width={80} />
          </TableRow>
        </TableHead>
        <TableBody>
          {reports.map((report) => (
            <TableRow data-testid={`report-row-${report.id}`} key={report.id}>
              <TableCell>
                <Text category="body-medium">{reportTypeName(report.type)}</Text>
              </TableCell>
              <TableCell>
                <Text category="body-medium">{describeFilters(report.params)}</Text>
              </TableCell>
              <TableCell>
                <Text category="body-medium">{formatDateTime(report.createdAt, i18n)}</Text>
              </TableCell>
              <TableCell>
                <Text category="body-medium">{report.requestedByName ?? ''}</Text>
              </TableCell>
              <TableCell>
                <Text category="body-medium">{statusLabel(report.status)}</Text>
              </TableCell>
              <TableCell>
                <Text category="body-medium">
                  {report.fileSizeBytes === null ? '----' : formatFileSize(report.fileSizeBytes)}
                </Text>
              </TableCell>
              <TableCell>{downloadCell(report)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!!data && data.lastPage > 0 && (
        <Stack alignItems="center" pt="16px">
          <Pagination
            count={data.lastPage + 1}
            onChange={(_, value) => setPage(value - 1)}
            page={page + 1}
          />
        </Stack>
      )}
      {errorDialog}
    </>
  );
};
